import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import { useCart } from "@/context/CartContext";
import { useLang } from "@/context/LanguageContext";
import { Minus, Plus, X, Loader2 } from "lucide-react";
import { useState } from "react";
import { createShopifyCheckout, fmt } from "@/lib/api";
import { toast } from "sonner";

export default function CartDrawer() {
  const { items, updateQty, removeItem, total, open, setOpen } = useCart();
  const { t } = useLang();
  const [loading, setLoading] = useState(false);

  const checkout = async () => {
    if (!items.length) return;
    setLoading(true);
    try {
      const payload = items.map((i) => ({ variantId: i.variantId, quantity: i.quantity }));
      const { checkoutUrl } = await createShopifyCheckout(payload);
      if (!checkoutUrl) throw new Error("no checkout url");
      window.location.href = checkoutUrl;
    } catch (e) {
      toast.error(t("cart.error"));
      setLoading(false);
    }
  };

  return (
    <Sheet open={open} onOpenChange={setOpen}>
      <SheetContent
        data-testid="cart-drawer"
        className="w-full sm:max-w-md border-l border-slate-200 bg-white text-slate-900 p-0 flex flex-col"
      >
        <SheetHeader className="px-6 pt-6 pb-4 border-b border-slate-200">
          <SheetTitle className="font-display font-extrabold text-3xl tracking-tight text-slate-900 flex items-center gap-3">
            {t("cart.title")}
            <span className="font-mono-x text-xs text-slate-400">({items.length})</span>
          </SheetTitle>
          <SheetDescription className="sr-only">Your shopping bag items and checkout</SheetDescription>
        </SheetHeader>

        <div className="flex-1 overflow-y-auto px-6 py-4 space-y-4">
          {items.length === 0 && (
            <div className="h-full flex flex-col items-center justify-center text-center py-20">
              <p className="font-display text-2xl mb-2 text-slate-900">{t("cart.empty")}</p>
              <p className="font-mono-x text-xs uppercase tracking-widest text-slate-400">{t("cart.emptySub")}</p>
            </div>
          )}
          {items.map((i) => (
            <div key={i.slug} data-testid={`cart-item-${i.slug}`} className="flex gap-4 rounded-xl border border-slate-200 bg-slate-50 p-3">
              <img src={i.image} alt={i.name} className="h-20 w-20 rounded-lg object-cover bg-white" />
              <div className="flex-1 min-w-0">
                <div className="flex items-start justify-between gap-2">
                  <h4 className="font-display font-bold text-lg leading-tight text-slate-900">{i.name}</h4>
                  <button data-testid={`remove-${i.slug}`} onClick={() => removeItem(i.slug)} className="text-slate-400 hover:text-slate-900 transition-colors">
                    <X className="h-4 w-4" />
                  </button>
                </div>
                <p className="font-mono-x text-sm font-bold text-[hsl(var(--accent))] mt-1">{fmt(i.price)}</p>
                <div className="mt-2 flex items-center gap-3">
                  <button data-testid={`dec-${i.slug}`} onClick={() => updateQty(i.slug, i.quantity - 1)} className="h-7 w-7 rounded-full border border-slate-300 flex items-center justify-center text-slate-600 hover:border-slate-900 hover:text-slate-900 transition-colors">
                    <Minus className="h-3 w-3" />
                  </button>
                  <span className="font-mono-x text-sm w-4 text-center">{i.quantity}</span>
                  <button data-testid={`inc-${i.slug}`} onClick={() => updateQty(i.slug, i.quantity + 1)} className="h-7 w-7 rounded-full border border-slate-300 flex items-center justify-center text-slate-600 hover:border-slate-900 hover:text-slate-900 transition-colors">
                    <Plus className="h-3 w-3" />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>

        <div className="border-t border-slate-200 px-6 py-6 space-y-4">
          <div className="flex items-center justify-between">
            <span className="font-mono-x text-xs uppercase tracking-widest text-slate-500">{t("cart.subtotal")}</span>
            <span data-testid="cart-total" className="font-display font-bold text-2xl text-slate-900">{fmt(total)}</span>
          </div>
          <button
            data-testid="checkout-btn"
            disabled={!items.length || loading}
            onClick={checkout}
            className="w-full rounded-full bg-[hsl(var(--accent))] text-white font-mono-x text-sm uppercase tracking-widest py-4 transition-all duration-300 hover:glow-orange disabled:opacity-40 disabled:cursor-not-allowed flex items-center justify-center gap-2"
          >
            {loading ? <><Loader2 className="h-4 w-4 animate-spin" /> {t("cart.redirecting")}</> : t("cart.checkout")}
          </button>
          <p className="text-center font-mono-x text-[10px] uppercase tracking-widest text-slate-400">{t("cart.secure")}</p>
        </div>
      </SheetContent>
    </Sheet>
  );
}
